import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TopNav } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, Clock, EyeOff, Users, Coins, Zap } from "lucide-react";

const PHASE_LABELS = { lobby: "In Lobby", live: "LIVE" };
const phaseLabel = (phase) => PHASE_LABELS[phase] || "Chiusa";

export default function JoinByLink() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user, ready, refresh, login, register } = useAuth();
  const [preview, setPreview] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  // "guest" | "login" | "register"
  const [mode, setMode] = useState("guest");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    api.get(`/rooms/by-code/${code}`)
      .then((r) => setPreview(r.data))
      .catch(() => toast.error("Codice sala non valido"));
  }, [code]);

  const doJoinAuth = async () => {
    if (!teamName.trim()) return toast.error("Inserisci il nome della tua squadra");
    setLoading(true);
    try {
      const { data } = await api.post("/rooms/join", { code: code.toUpperCase(), team_name: teamName });
      toast.success("Entrato nella sala!");
      nav(data.phase === "live" ? `/rooms/${data.id}/live` : `/rooms/${data.id}/lobby`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doJoinAsGuest = async () => {
    if (!teamName.trim()) return toast.error("Inserisci il nome della tua squadra");
    setLoading(true);
    try {
      const { data } = await api.post("/rooms/join-as-guest", {
        code: code.toUpperCase(),
        team_name: teamName,
        display_name: displayName || teamName,
      });
      // Refresh the auth context so /auth/me picks up the guest cookie
      if (refresh) await refresh();
      toast.success("Benvenuto! Sei nella sala.");
      const r = data.room;
      nav(r.phase === "live" ? `/rooms/${r.id}/live` : `/rooms/${r.id}/lobby`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || email.split("@")[0]);
      toast.success("Accesso effettuato!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!ready) return <div><TopNav /><div className="p-10 text-center text-slate-400">Caricamento…</div></div>;

  const submitLabel = mode === "login" ? "Accedi e continua" : "Crea account e continua";

  return (
    <div>
      <TopNav />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="label-eyebrow mb-2">Invito ricevuto</div>
        <h1 className="text-4xl font-black tracking-tight mb-2">Entra nella sala d'asta</h1>
        <p className="text-slate-400 mb-8">Sei stato invitato dal creatore della lega. Un click e sei dentro — non serve registrarsi.</p>

        {preview ? (
          <div className="glass rounded-2xl p-6 mb-6" data-testid="join-preview">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-lg font-black">{preview.name}</div>
                  <div className="text-xs text-slate-400 font-mono">Codice {preview.code}</div>
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded ${preview.phase === "lobby" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>
                {phaseLabel(preview.phase)}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-slate-900/40 border border-white/5">
                <Users className="w-4 h-4 text-emerald-400 mb-1" />
                <div className="text-[10px] uppercase text-slate-500 tracking-widest font-bold">Squadre</div>
                <div className="font-bold">{preview.team_slots.length}</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/40 border border-white/5">
                <Coins className="w-4 h-4 text-emerald-400 mb-1" />
                <div className="text-[10px] uppercase text-slate-500 tracking-widest font-bold">Crediti</div>
                <div className="font-bold">{preview.initial_credits} FM</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/40 border border-white/5">
                <Clock className="w-4 h-4 text-emerald-400 mb-1" />
                <div className="text-[10px] uppercase text-slate-500 tracking-widest font-bold">Timer</div>
                <div className="font-bold">{preview.timer_seconds}s</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/40 border border-white/5">
                {preview.auction_mode === "secret" ? <EyeOff className="w-4 h-4 text-amber-400 mb-1" /> : <Trophy className="w-4 h-4 text-emerald-400 mb-1" />}
                <div className="text-[10px] uppercase text-slate-500 tracking-widest font-bold">Modalità</div>
                <div className="font-bold capitalize">{preview.auction_mode === "secret" ? "Segreta" : "Classica"}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 mb-6 text-slate-400 text-center">Caricamento preview…</div>
        )}

        {user ? (
          <div className="glass rounded-2xl p-6" data-testid="team-name-form">
            <Label className="text-slate-300">Nome della tua squadra</Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="join-team-input" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="es. Real Fanta United" className="bg-slate-900/60 border-white/10 h-11" />
              <Button data-testid="join-submit-btn" onClick={doJoinAuth} disabled={loading} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11 px-6">
                {loading ? "…" : "Entra"}
              </Button>
            </div>
            <div className="text-xs text-slate-500 mt-2">Puoi usare un nome nuovo oppure quello di uno slot già preparato dall'admin.</div>
          </div>
        ) : (
          <div className="glass rounded-2xl p-6" data-testid="guest-join-form">
            <div className="flex gap-2 mb-4">
              <button onClick={() => setMode("guest")}
                      className={`flex-1 h-10 rounded-lg text-sm font-bold ${mode === "guest" ? "bg-emerald-500 text-slate-950" : "bg-slate-900/60 text-slate-300"}`}
                      data-testid="tab-guest">
                <Zap className="w-4 h-4 inline mr-1" />Entra subito
              </button>
              <button onClick={() => setMode("login")}
                      className={`flex-1 h-10 rounded-lg text-sm font-bold ${mode === "login" ? "bg-emerald-500 text-slate-950" : "bg-slate-900/60 text-slate-300"}`}
                      data-testid="tab-login">Accedi</button>
              <button onClick={() => setMode("register")}
                      className={`flex-1 h-10 rounded-lg text-sm font-bold ${mode === "register" ? "bg-emerald-500 text-slate-950" : "bg-slate-900/60 text-slate-300"}`}
                      data-testid="tab-register">Registrati</button>
            </div>

            {mode === "guest" ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300">Nome della tua squadra</Label>
                  <Input data-testid="guest-team-input"
                         value={teamName} onChange={(e) => setTeamName(e.target.value)}
                         placeholder="es. Real Fanta United"
                         className="bg-slate-900/60 border-white/10 h-11 mt-2" />
                </div>
                <div>
                  <Label className="text-slate-300">Il tuo nome (opzionale)</Label>
                  <Input data-testid="guest-display-input"
                         value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                         placeholder="Come vuoi essere chiamato"
                         className="bg-slate-900/60 border-white/10 h-11 mt-2" />
                </div>
                <Button data-testid="guest-join-btn" onClick={doJoinAsGuest} disabled={loading}
                        className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11">
                  <Zap className="w-4 h-4 mr-2" />{loading ? "…" : "Entra nella sala"}
                </Button>
                <p className="text-xs text-slate-500 text-center">Nessuna registrazione: entri con un cookie temporaneo valido 7 giorni.</p>
              </div>
            ) : (
              <form onSubmit={doAuth} className="space-y-3" autoComplete="off">
                {mode === "register" && (
                  <Input placeholder="Nome" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-900/60 border-white/10 h-11" data-testid="inline-name" />
                )}
                <Input type="email" required autoComplete="off" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-slate-900/60 border-white/10 h-11" data-testid="inline-email" />
                <Input type="password" required minLength={6} autoComplete="new-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-slate-900/60 border-white/10 h-11" data-testid="inline-password" />
                <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11" data-testid="inline-submit">
                  {loading ? "…" : submitLabel}
                </Button>
              </form>
            )}
          </div>
        )}

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-400">← Torna alla home</Link>
        </div>
      </div>
    </div>
  );
}
